import type { LanesConfig } from "@repo/lanes";
import {
  Combobox,
  ComboboxCheckboxItem,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
} from "@repo/ui/combobox";
import { Icon } from "@repo/ui/components/icon";
import { Token } from "@repo/ui/components/token";
import { Button } from "@repo/ui/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@repo/ui/ui/field";
import { Spinner } from "@repo/ui/ui/spinner";
import { Textarea } from "@repo/ui/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@repo/ui/ui/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@repo/ui/ui/tooltip";
import { useStore } from "@tanstack/react-form";
import { useCallback, useRef, useState } from "react";

import { listDatasourceSchemas } from "~/stores/datasources/datasources.actions";
import type {
  DatasourceForm,
  SnowflakeAuthMethod,
} from "./datasource-new.types";
import { snowflakeAuthCredentials } from "./datasource-new.types";

const PRIVATE_KEY_PLACEHOLDER = `-----BEGIN PRIVATE KEY-----
MIIEvQIBADANBgkqhkiG9w0BAQEFAASCBKcwggSjAgEAAoIBAQC...
-----END PRIVATE KEY-----`;

type SchemaOption = { name: string; comment?: string | null };
type SchemasStatus = "idle" | "loading" | "error";

/**
 * Multi-select schema picker for Snowflake. Lists the schemas available on the
 * connection (via `datasources.listSchemas`) once the connection credentials
 * are filled in, and lets the user pick which schemas to sync. Fetches on the
 * first open; a refresh action re-fetches (e.g. after changing credentials).
 */
const SnowflakeSchemasField = ({ form }: { form: DatasourceForm }) => {
  const [status, setStatus] = useState<SchemasStatus>("idle");
  const [options, setOptions] = useState<SchemaOption[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [open, setOpen] = useState(false);
  // Set while a fetch is in-flight in response to the user trying to open the
  // dropdown, so we can open it only once the data has actually arrived (and
  // never flash an empty popup when the connection can't be reached).
  const openAfterFetch = useRef(false);

  const creds = useStore(form.store, (s) => ({
    account: s.values.account,
    username: s.values.username,
    authMethod: s.values.authMethod,
    password: s.values.password,
    privateKey: s.values.privateKey,
    privateKeyPass: s.values.privateKeyPass,
    warehouse: s.values.warehouse,
    database: s.values.database,
    role: s.values.role,
  }));

  const hasAuth =
    creds.authMethod === "keypair"
      ? Boolean(creds.privateKey)
      : Boolean(creds.password);

  // The credentials `listSchemas` needs to introspect the connection.
  const canFetch = Boolean(
    creds.account &&
      creds.username &&
      hasAuth &&
      creds.warehouse &&
      creds.database,
  );

  const fetchSchemas = useCallback(async () => {
    if (!canFetch) return;
    setStatus("loading");
    try {
      const result = await listDatasourceSchemas({
        type: "SNOWFLAKE",
        credentials: {
          account: creds.account,
          warehouse: creds.warehouse,
          database: creds.database,
          username: creds.username,
          ...snowflakeAuthCredentials(creds),
          ...(creds.role ? { role: creds.role } : {}),
        },
      } as LanesConfig);
      setOptions(result);
      setHasFetched(true);
      setStatus("idle");
      if (openAfterFetch.current) setOpen(true);
    } catch {
      setStatus("error");
    } finally {
      openAfterFetch.current = false;
    }
  }, [
    canFetch,
    creds.account,
    creds.username,
    creds.authMethod,
    creds.password,
    creds.privateKey,
    creds.privateKeyPass,
    creds.warehouse,
    creds.database,
    creds.role,
  ]);

  return (
    <form.Field name="schemas">
      {(field) => {
        const selected = field.state.value ?? [];
        const isInvalid =
          field.state.meta.isTouched && !field.state.meta.isValid;
        // Disable the trigger while we can't fetch or a fetch is in-flight so
        // the dropdown never opens onto an empty/failed list.
        const triggerDisabled = !canFetch || status === "loading";

        const handleOpenChange = (next: boolean) => {
          if (!next) {
            setOpen(false);
            field.handleBlur();
            return;
          }
          if (triggerDisabled) return;
          // First open (or retry after an error): fetch, then open on success.
          if (status === "error" || !hasFetched) {
            openAfterFetch.current = true;
            fetchSchemas();
            return;
          }
          setOpen(true);
        };

        return (
          <Field data-invalid={isInvalid}>
            <FieldLabel>Schemas</FieldLabel>
            <TooltipProvider delay={300}>
              <Combobox
                multiple
                open={open}
                items={options.map((o) => o.name)}
                value={selected}
                onValueChange={(value) => field.handleChange(value as string[])}
                onOpenChange={handleOpenChange}
              >
                <ComboboxTrigger
                  render={
                    <Button
                      variant="outline"
                      className="w-full justify-start font-normal"
                      disabled={triggerDisabled}
                      aria-invalid={isInvalid || status === "error"}
                    >
                      <span className="flex flex-1 items-center gap-1.5 text-left">
                        {status === "loading" ? (
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <Spinner className="size-4" />
                            Loading schemas...
                          </span>
                        ) : status === "error" ? (
                          <span className="text-destructive">
                            Couldn't load schemas — retry
                          </span>
                        ) : selected.length > 0 ? (
                          <span>
                            {selected.length === 1
                              ? "1 schema"
                              : `${selected.length} schemas`}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">
                            {canFetch
                              ? "Select schemas"
                              : "Fill in connection details first"}
                          </span>
                        )}
                      </span>
                      <Icon
                        name={
                          status === "error"
                            ? "arrow-left-counter-clockwise"
                            : "chevron-down"
                        }
                        className="ml-auto"
                      />
                    </Button>
                  }
                />
                <ComboboxContent className="min-w-(--anchor-width)">
                  {status === "loading" ? (
                    <div className="flex items-center gap-2 px-3 py-4 text-muted-foreground text-xs">
                      <Spinner className="size-4" />
                      Loading schemas...
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-1 p-1">
                        <ComboboxInput
                          showTrigger={false}
                          placeholder="Search schemas..."
                          className="flex-1"
                        />
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={fetchSchemas}
                                aria-label="Refresh"
                              />
                            }
                          >
                            <Icon name="arrow-left-counter-clockwise" />
                          </TooltipTrigger>
                          <TooltipContent>Refresh</TooltipContent>
                        </Tooltip>
                      </div>
                      <ComboboxSeparator />
                      <ComboboxEmpty>No schemas found.</ComboboxEmpty>
                      <ComboboxList>
                        {(name: string) => {
                          const option = options.find((o) => o.name === name);
                          return (
                            <ComboboxCheckboxItem key={name} value={name}>
                              <span className="min-w-0 flex-1 truncate">
                                {name}
                              </span>
                              {option?.comment ? (
                                <Tooltip>
                                  <TooltipTrigger
                                    render={
                                      <span className="ml-auto inline-flex shrink-0" />
                                    }
                                  >
                                    <Icon
                                      name="info-circle"
                                      className="text-muted-foreground"
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent side="right">
                                    {option.comment}
                                  </TooltipContent>
                                </Tooltip>
                              ) : null}
                            </ComboboxCheckboxItem>
                          );
                        }}
                      </ComboboxList>
                    </>
                  )}
                </ComboboxContent>
              </Combobox>
            </TooltipProvider>
            {isInvalid ? (
              <FieldError errors={field.state.meta.errors} />
            ) : (
              <FieldDescription>
                {selected.length > 0 ? (
                  <span className="flex flex-wrap items-center gap-1">
                    {selected.map((name) => (
                      <Token key={name}>{name}</Token>
                    ))}
                  </span>
                ) : (
                  "Choose which schemas to sync for this database."
                )}
              </FieldDescription>
            )}
          </Field>
        );
      }}
    </form.Field>
  );
};

const AUTH_METHOD_LABELS: Record<SnowflakeAuthMethod, string> = {
  password: "Password",
  keypair: "Key pair",
};

/** Segmented control choosing between password and RSA key-pair authentication. */
const SnowflakeAuthMethodField = ({ form }: { form: DatasourceForm }) => (
  <form.Field name="authMethod">
    {(field) => (
      <Field>
        <FieldLabel>Authentication</FieldLabel>
        <ToggleGroup
          variant="outline"
          className="w-full"
          value={[field.state.value]}
          onValueChange={(value) => {
            const next = value[0] as SnowflakeAuthMethod | undefined;
            if (next) field.handleChange(next);
          }}
        >
          {(Object.keys(AUTH_METHOD_LABELS) as SnowflakeAuthMethod[]).map(
            (method) => (
              <ToggleGroupItem key={method} value={method} className="flex-1">
                {AUTH_METHOD_LABELS[method]}
              </ToggleGroupItem>
            ),
          )}
        </ToggleGroup>
        <FieldDescription>
          Key-pair auth uses a PEM-encoded RSA private key whose public key is
          registered on the Snowflake user.
        </FieldDescription>
      </Field>
    )}
  </form.Field>
);

/** PEM private key input, shown when key-pair authentication is selected. */
const SnowflakePrivateKeyField = ({ form }: { form: DatasourceForm }) => (
  <form.Field name="privateKey">
    {(field) => {
      const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
      return (
        <Field data-invalid={isInvalid}>
          <FieldLabel htmlFor={field.name}>Private key</FieldLabel>
          <Textarea
            id={field.name}
            name={field.name}
            value={field.state.value}
            onBlur={field.handleBlur}
            onChange={(e) => field.handleChange(e.target.value)}
            placeholder={PRIVATE_KEY_PLACEHOLDER}
            autoComplete="off"
            aria-invalid={isInvalid}
            className="no-scrollbar h-40 resize-none overflow-x-auto whitespace-pre font-mono text-sm"
          />
          {isInvalid ? (
            <FieldError errors={field.state.meta.errors} />
          ) : (
            <FieldDescription>
              Paste the full PKCS#8 key, including the BEGIN and END lines.
            </FieldDescription>
          )}
        </Field>
      );
    }}
  </form.Field>
);

/** Snowflake credential fields. */
export const SnowflakeFields = ({ form }: { form: DatasourceForm }) => {
  const authMethod = useStore(form.store, (s) => s.values.authMethod);
  const isKeyPair = authMethod === "keypair";

  return (
    <FieldGroup>
      <form.AppField name="account">
        {(field) => (
          <field.TextField label="Account" placeholder="xy12345.us-east-1" />
        )}
      </form.AppField>

      <SnowflakeAuthMethodField form={form} />

      <div className="grid grid-cols-2 gap-4">
        <form.AppField name="username">
          {(field) => <field.TextField label="Username" placeholder="admin" />}
        </form.AppField>

        {isKeyPair ? (
          <form.AppField name="privateKeyPass">
            {(field) => (
              <field.PasswordField
                label="Key passphrase"
                placeholder="••••••••"
                description="Only needed if the key is encrypted."
              />
            )}
          </form.AppField>
        ) : (
          <form.AppField name="password">
            {(field) => (
              <field.PasswordField label="Password" placeholder="••••••••" />
            )}
          </form.AppField>
        )}
      </div>

      {isKeyPair && <SnowflakePrivateKeyField form={form} />}

      <div className="grid grid-cols-2 gap-4">
        <form.AppField name="warehouse">
          {(field) => (
            <field.TextField label="Warehouse" placeholder="COMPUTE_WH" />
          )}
        </form.AppField>

        <form.AppField name="role">
          {(field) => (
            <field.TextField label="Role" placeholder="ACCOUNTADMIN" />
          )}
        </form.AppField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <form.AppField name="database">
          {(field) => (
            <field.TextField label="Database" placeholder="MY_DATABASE" />
          )}
        </form.AppField>

        <SnowflakeSchemasField form={form} />
      </div>
    </FieldGroup>
  );
};
